<template>
    <div class='pa-5'>
        <v-card v-if='data'>
            <v-card-title>
                <h1 class='title'>{{ 'Departments' | term }}</h1>
                <v-btn small color='secondary' class='ml-3' @click='create()' v-if='!!selectedMineArea'>
                    <v-icon left size='medium'>mdi-plus</v-icon>
                    {{ $t('shared.button.add') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-select
                    :items='mineAreas'
                    item-value='id'
                    item-text='name'
                    v-model='selectedMineArea'
                    :label="$termSync('Mine Area')"
                    single-line
                    hide-details
                    class='admin-table-filter'
                    :menu-props='{ dark: true }'
                ></v-select>
            </v-card-title>
            <v-data-table
                disable-pagination
                hide-default-footer
                :headers='headers'
                :items='filtered'
                v-if='!!selectedMineArea'
            >
                <template v-slot:item.id='{ item }'>
                    <v-btn text icon @click='edit(item)' class='float-right'>
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn text icon @click='editSubscribers(item)' class='float-right'>
                        <v-icon>mdi-bell-plus</v-icon>
                    </v-btn>
                </template>
            </v-data-table>
            <empty-state
                v-if='!selectedMineArea'
                icon='mdi-alert-circle-outline'
                :message="$t('shared.message.select-a', { term: $termSync('Mine Area') })"
            ></empty-state>
        </v-card>
        <department-creation-wizard
            :initial-information='newDepartment'
            v-if='newDepartment'
            @save='saveNewDepartment'
            @cancel='cancelNewDepartment'
        ></department-creation-wizard>
        <standard-dialog :render='!!selected' :value='showModal' :max-width='900' persistent>
            <template v-slot:title>
                <span v-if='selected.id'>{{ $t('shared.button.edit') }}</span>
                <span v-else>{{ $t('shared.button.create') }}</span>
                <span class='ml-2'>{{ 'Department' | term }}</span>
            </template>
            <template v-slot:content>
                <v-form ref='form' :lazy-validation='true'>
                    <v-container fluid>
                        <v-row>
                            <v-col cols='12' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :label="$t('shared.label.name')"
                                    :rules='[$rules.required(), $rules.maxLength(50)]'
                                    v-model='selected.name'
                                    counter='50'
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols='4' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :label="$t('departments.location-name')"
                                    :rules='[$rules.required(), $rules.maxLength(50)]'
                                    v-model='selected.locationName'
                                    counter='50'
                                    hint='e.g. Heading'
                                ></v-text-field>
                            </v-col>
                            <v-col cols='4' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :label="$t('departments.location-name-plural')"
                                    :rules='[$rules.required(), $rules.maxLength(50)]'
                                    v-model='selected.locationPluralName'
                                    counter='50'
                                    hint='e.g. Headings'
                                ></v-text-field>
                            </v-col>
                            <v-col cols='4' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :label="$t('departments.location-name-abbreviation')"
                                    :rules='[$rules.required(), $rules.maxLength(5)]'
                                    v-model='selected.locationAbbrName'
                                    counter='5'
                                    hint="e.g. 'HEAD'"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row v-if='selected.id == null'>
                            <v-col cols='4' class='pa-2'>
                                <v-select
                                    autocomplete='off'
                                    multiple
                                    :label="$t('departments.intended-use')"
                                    :rules='[$rules.required()]'
                                    v-model='selected.intendedUses'
                                    :items='boardIntendedUses'
                                    item-value='id'
                                    item-text='name'
                                    hide-details
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols='4' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :label="$t('departments.cycle-name')"
                                    :rules='[$rules.maxLength(50)]'
                                    v-model='selected.cycleName'
                                    counter='50'
                                    hint='e.g. Cut'
                                ></v-text-field>
                            </v-col>
                            <v-col cols='4' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :label="$t('departments.cycle-name-plural')"
                                    :rules='[$rules.maxLength(50)]'
                                    v-model='selected.cyclePluralName'
                                    counter='50'
                                    hint='e.g. Cuts'
                                ></v-text-field>
                            </v-col>
                            <v-col cols='4' class='pa-2'></v-col>
                        </v-row>
                        <v-row v-if='!filteredTasks.length'>
                            <v-col cols='12'>
                                <v-alert color='grey lighten-2'>
                                    <strong>{{ $t('departments.note') }}</strong> {{ $t('departments.departments-require-some-specific-configuration-based-on-tasks-no-tasks-exist-for-this-department-yet-configure-tasks-and-then-update-this-department') }}
                                </v-alert>
                            </v-col>
                        </v-row>
                        <v-row v-if='filteredTasks.length'>
                            <v-col cols='4' class='pa-2'>
                                <v-checkbox
                                    autocomplete='off'
                                    :label="$t('departments.sos-tasks')"
                                    v-model='selected.hasStartOfShiftSpecialTasks'
                                ></v-checkbox>
                            </v-col>
                            <v-col cols='4' class='pa-2' v-if='selected.hasStartOfShiftSpecialTasks'>
                                <div v-for='(task, index) in selected.startOfShiftTaskTypesModel' :key='index'>
                                    <v-select
                                        autocomplete='off'
                                        :label="$t('departments.task-type')"
                                        v-model='selected.startOfShiftTaskTypesModel[index]'
                                        :items='filteredTasks'
                                        item-value='id'
                                        item-text='name'
                                        hide-details
                                        append-outer-icon='mdi-delete'
                                        @click:append-outer='selected.startOfShiftTaskTypesModel.splice(index, 1)'
                                    ></v-select>
                                </div>
                                <v-btn class='mt-3' @click='selected.startOfShiftTaskTypesModel.push(null)'
                                >{{ $t('shared.button.add') }}
                                </v-btn
                                >
                            </v-col>
                            <v-col cols='4' class='pa-2'></v-col>
                            <v-col cols='4' class='pa-2'>
                                <v-checkbox
                                    autocomplete='off'
                                    :label="$t('departments.eos-task')"
                                    v-model='selected.hasEndOfShiftSpecialTask'
                                ></v-checkbox>
                            </v-col>
                            <v-col cols='4' class='pa-2'>
                                <v-select
                                    autocomplete='off'
                                    :label="$t('departments.task-type')"
                                    :disabled='!selected.hasEndOfShiftSpecialTask'
                                    :rules='[$rules.requiredIf(() => selected.hasEndOfShiftSpecialTask)]'
                                    v-model='selected.endOfShiftTaskType'
                                    :items='filteredTasksForEoSTask'
                                    item-value='id'
                                    item-text='name'
                                    hide-details
                                ></v-select>
                            </v-col>
                            <v-col cols='4' class='pa-2'>
                            </v-col>
                        </v-row>
                        <v-subheader>{{ $t('departments.config') }}</v-subheader>
                        <v-row>
                            <v-col cols='4' class='pa-2'>
                                <v-checkbox autocomplete='off' :label="$t('departments.enforce-cycles-on-development-board')"
                                            :input-value='!selected.allowDevelopmentManualTasks'
                                            @change='(val)=>selected.allowDevelopmentManualTasks=!val'></v-checkbox>
                            </v-col>
                            <v-col cols='4' class='pa-2'>
                                <v-checkbox autocomplete='off' :label="$t('departments.show-on-mine-interaction-board')"
                                            :input-value='selected.includeOnInteractionsBoard'
                                            @change='(val)=>selected.includeOnInteractionsBoard=val'></v-checkbox>
                            </v-col>
                            <v-col cols='4' class='pa-2'>
                                <v-checkbox
                                    autocomplete='off'
                                    :label="$t('departments.locations-in-resource-planning')"
                                    v-model='selected.showLocationsInResourcePlanning'
                                ></v-checkbox>
                            </v-col>
                            <v-col cols='4' class='pa-2'>
                                <v-text-field
                                    type="number"
                                    :label="$t('departments.cut-number-priority-cutoff')"
                                    v-model="selected.maxCutInfoPriority"
                                    :rules="[$rules.min(0), $rules.int()]"
                                >
                                    <template v-slot:append-outer>
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on }">
                                                <v-icon v-on="on" class="ml-1">mdi-information-outline</v-icon>
                                            </template>
                                            <span>{{ $t('departments.hide-cut-number-chainage-hint') }}</span>
                                        </v-tooltip>
                                    </template>
                                </v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </template>
            <template v-slot:actions>
                <v-btn color='red' text v-if='selected && selected.id' @click='del()'>{{ $t('shared.button.delete') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <v-btn color='primary' text @click='save()'>{{ $t('shared.button.save') }}</v-btn>
            </template>
            <template v-slot:offline-actions>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <offline-btn text></offline-btn>
            </template>
        </standard-dialog>
        <subscribers-dialog
            ref='subscribersDialog'
        >

        </subscribers-dialog>
        <confirm ref='confirm'></confirm>
    </div>
</template>
<script>
import { EventBus, Events } from '@/lib/EventBus';
import Departments from '@/lib/data/Departments';
import MineAreas from '@/lib/data/MineAreas';
import TaskTypes from '@/lib/data/TaskTypes';
import SubscribersDialog from '@/components/Admin/SubscribersDialog.vue';
import { DepartmentFeatureFlags, IntendedBoardUse, TaskClasses } from '@/models/api';
import { enumOptions } from '@/lib/EnumExtensions';
import DepartmentCreationWizard from '@/components/Admin/Shared/DepartmentCreationWizard.vue';
import LocationCreationWizard from '@/components/Shared/Locations/LocationCreationWizard.vue';
import { DepartmentHasFeature } from '@/lib/services/Department';

export default {
    components: { DepartmentCreationWizard, SubscribersDialog },
    data() {
        return {
            data: null,
            mineAreas: null,
            selectedMineArea: null,
            showModal: false,
            selected: null,
            newDepartment: null,
            boardIntendedUses: enumOptions(IntendedBoardUse, this.$options.filters.intendedBoardUses),
            headers: [
                { text: 'Name', value: 'name' },
                { text: this.$t('departments.short-code'), value: 'short' },
                { text: '', value: 'id' }
            ]
        };
    },
    async created() {
        await this.loadData();
    },
    destroyed() {
    },
    methods: {
        async loadData() {
            this.mineAreas = await MineAreas.get();
            this.data = await Departments.get();
            for (let x of this.data) {
                x.short = x.name[0];
            }

            this.taskTypes = await TaskTypes.get();
        },
        create() {
            this.newDepartment = {};
        },
        edit(item) {
            this.selected = { ...item, showLocationsInResourcePlanning: DepartmentHasFeature(item, DepartmentFeatureFlags.LocationsInResourceBoard) };
            this.showModal = true;
            setTimeout(() => this.$refs.form.resetValidation(), 1);
        },
        async editSubscribers(item) {
            await this.$refs.subscribersDialog.show(
                async () => {
                    return await Departments.getSubscribersById(item.id);
                },
                async (subscribers) => {
                    await Departments.updateSubscribers({
                        _type: 'UpdateDepartmentNotificationSubscribersCommand',
                        departmentId: item.id,
                        subscribers: subscribers
                    });
                });
        },
        async save() {
            if (!this.$refs.form.validate()) {
                return;
            }

            await Departments.update({
                _type: 'UpdateDepartmentCommand',
                id: this.selected.id,
                name: this.selected.name,
                locationName: this.selected.locationName,
                locationPluralName: this.selected.locationPluralName,
                locationAbbrName: this.selected.locationAbbrName,
                cycleName: this.selected.cycleName,
                cyclePluralName: this.selected.cyclePluralName,
                hasStartOfShiftSpecialTasks: this.selected.hasStartOfShiftSpecialTasks,
                startOfShiftTaskTypes: this.selected.startOfShiftTaskTypesModel,
                hasEndOfShiftSpecialTask: this.selected.hasEndOfShiftSpecialTask,
                endOfShiftTaskTypeId: this.selected.endOfShiftTaskType,
                allowDevelopmentManualTasks: this.selected.allowDevelopmentManualTasks,
                includeOnInteractionsBoard: this.selected.includeOnInteractionsBoard,
                showLocationsInResourcePlanning: this.selected.showLocationsInResourcePlanning,
                maxCutInfoPriority: this.selected.maxCutInfoPriority?.length === 0 ? null : this.selected.maxCutInfoPriority,
            });

            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Department')} Saved`);
            this.showModal = false;
            await this.loadData();
        },

        async saveNewDepartment(department) {
            this.newDepartment = null;

            await Departments.create({
                _type: 'CreateDepartmentCommand',
                mineAreaId: this.selectedMineArea,
                name: department.name,
                locationName: department.locationName,
                locationPluralName: department.locationPluralName,
                locationAbbrName: department.locationAbbrName,
                intendedUses: department.intendedUses,
                cycleName: department.cycleName,
                cyclePluralName: department.cyclePluralName,
                hasStartOfShiftSpecialTasks: false,
                startOfShiftTaskTypes: [],
                hasEndOfShiftSpecialTask: false,
                endOfShiftTaskTypeId: null,
                allowDevelopmentManualTasks: department.allowDevelopmentManualTasks,
                includeOnInteractionsBoard: department.includeOnInteractionsBoard,
                maxCutInfoPriority: department.maxCutInfoPriority?.length === 0 ? null : department.maxCutInfoPriority,
            });

            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Department')} Created`);

            await this.loadData();
        },
        cancelNewDepartment() {
            this.newDepartment = null;
        },
        async del() {
            if (await this.$refs.confirm.openAsDeleteResource(this.$termSync('Department'), {})) {
                await Departments.delete(this.selected.id);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Department')} Deleted`);
                this.showModal = false;
                await this.loadData();
            }
        }
    },
    computed: {
        filtered() {
            if (!this.data || !this.selectedMineArea) {
                return [];
            }
            return this.data.filter(x => x.mineAreaId == this.selectedMineArea);
        },
        filteredTasks() {
            if (!this.taskTypes || !this.selected) {
                return [];
            }
            return this.taskTypes.filter(x => x.departmentId == this.selected.id);
        },
        filteredTasksForEoSTask() {
            return this.filteredTasks.filter(x => x.taskClass === TaskClasses.Fire ||
                x.id === this.selected.endOfShiftTaskType);
        }
    }
};
</script>
